import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Heart, Users, Award, Shield, Target, Sparkles } from 'lucide-react';
import { images } from '../data/mock';

const Vision = () => {
  const { t } = useLanguage();

  const iconMap = {
    "Intégrité": Shield,
    "Entegrite": Shield,
    "Discipline": Target,
    "Disiplin": Target,
    "Solidarité": Users,
    "Solidarite": Users,
    "Excellence": Award,
    "Ekselans": Award,
    "Responsabilité": Heart,
    "Responsablite": Heart,
    "Inclusion": Sparkles,
    "Enklizyon": Sparkles
  };

  return (
    <section id="vision" className="relative py-24 bg-gradient-to-b from-gray-950 to-gray-900">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Vision Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-6">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-400">2026-2030</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t.vision.title}
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t.vision.subtitle}
          </p>
        </div>

        {/* Mission Card */}
        <div className="mb-16 relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-red-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
          <div className="relative bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 sm:p-12 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-white mb-4">{t.vision.mission}</h3>
              <p className="text-lg text-gray-300 leading-relaxed">
                {t.vision.missionText}
              </p>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="mb-12">
          <h3 className="text-3xl font-bold text-white text-center mb-12">{t.vision.values}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.vision.valuesList.map((value, index) => {
              const Icon = iconMap[value.name] || Shield;
              return (
                <div
                  key={index}
                  className="group relative bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-red-500/0 group-hover:from-blue-500/10 group-hover:to-red-500/10 rounded-xl transition-all duration-300" />
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-2">{value.name}</h4>
                    <p className="text-gray-400 leading-relaxed">{value.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
          {[images.stadium2, images.ball1, images.stadium4, images.haiti1].map((img, index) => (
            <div
              key={index}
              className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer"
            >
              <img
                src={img}
                alt={`Vision ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950/80 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Vision;
